import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { refreshToken, signOut } from '../redux/actions/authActions';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button } from '@mui/material';

// Configuration constants
const WARNING_BEFORE_EXPIRY = 2 * 60 * 1000; // 2 minutes warning before token expires
const POPUP_TIMEOUT = 60 * 1000; // 60 seconds to respond to popup
const AUTO_REFRESH_BEFORE_EXPIRY = 5 * 60 * 1000; // Auto refresh 5 minutes before expiry
const ACTIVITY_CHECK_INTERVAL = 30 * 1000; // Check activity every 30 seconds

export const useActivityTracker = () => {
  const dispatch = useDispatch();
  const timeoutRef = useRef(null);
  const logoutTimeoutRef = useRef(null);
  const autoRefreshTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const [showPopup, setShowPopup] = useState(false);
  const expirationTimeRef = useRef(null);
  const isActiveRef = useRef(true);

  // Get token from Redux state or session storage
  const token = useSelector(
    (state) => state.auth?.token || sessionStorage.getItem("token")
  );

  // Function to parse JWT token and extract expiration time
  const getTokenExpiration = (token) => {
    if (!token) return null;

    try {
      const payload = token.split('.')[1];
      const decodedPayload = JSON.parse(atob(payload));
      return decodedPayload.exp * 1000; // Convert to milliseconds
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  };

  // Track user activity
  const updateActivity = () => {
    lastActivityRef.current = Date.now();
    isActiveRef.current = true;
  };

  // Check if user has been inactive
  const isUserInactive = () => {
    const inactivityPeriod = 5 * 60 * 1000; // 5 minutes of inactivity
    return Date.now() - lastActivityRef.current > inactivityPeriod;
  };

  // Function to refresh token
  const refreshUserToken = async () => {
    try {
      // Clear any pending logout
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
        logoutTimeoutRef.current = null;
      }

      const newToken = await dispatch(refreshToken());
      if (newToken) {
        setupExpirationHandling(newToken);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to refresh token:", error);
      return false;
    }
  };

  // Function to setup expiration handling
  const setupExpirationHandling = (token) => {
    // Clear all existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
    if (autoRefreshTimeoutRef.current) {
      clearTimeout(autoRefreshTimeoutRef.current);
      autoRefreshTimeoutRef.current = null;
    }

    const expirationTime = getTokenExpiration(token);
    if (!expirationTime) return;

    expirationTimeRef.current = expirationTime;

    const currentTime = Date.now();
    const timeUntilExpiry = expirationTime - currentTime;

    // If token is already expired, logout immediately
    if (timeUntilExpiry <= 0) {
      dispatch(signOut());
      return;
    }

    // Setup auto-refresh for active users
    const timeUntilAutoRefresh = Math.max(0, timeUntilExpiry - AUTO_REFRESH_BEFORE_EXPIRY);

    autoRefreshTimeoutRef.current = setTimeout(() => {
      // Only auto-refresh if user is active
      if (!isUserInactive() && isActiveRef.current) {
        refreshUserToken();
      } else {
        // If user is inactive, setup warning instead
        setupWarning(token);
      }
    }, timeUntilAutoRefresh);

    // Also setup warning as fallback
    const timeUntilWarning = Math.max(0, timeUntilExpiry - WARNING_BEFORE_EXPIRY);

    timeoutRef.current = setTimeout(() => {
      // Only show popup if we haven't auto-refreshed
      if (!autoRefreshTimeoutRef.current) {
        setShowPopup(true);

        logoutTimeoutRef.current = setTimeout(() => {
          setShowPopup(false);
          dispatch(signOut());
        }, POPUP_TIMEOUT);
      }
    }, timeUntilWarning);
  };

  // Setup warning without auto-refresh
  const setupWarning = (token) => {
    const expirationTime = getTokenExpiration(token);
    if (!expirationTime) return;

    const currentTime = Date.now();
    const timeUntilExpiry = expirationTime - currentTime;
    const timeUntilWarning = Math.max(0, timeUntilExpiry - WARNING_BEFORE_EXPIRY);

    timeoutRef.current = setTimeout(() => {
      setShowPopup(true);

      logoutTimeoutRef.current = setTimeout(() => {
        setShowPopup(false);
        dispatch(signOut());
      }, POPUP_TIMEOUT);
    }, timeUntilWarning);
  };

  // Handle continue session
  const handleContinueSession = () => {
    setShowPopup(false);
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }

    updateActivity();
    refreshUserToken();
  };

  // Handle visibility change
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      updateActivity();

      if (expirationTimeRef.current) {
        const currentTime = Date.now();

        if (currentTime >= expirationTimeRef.current) {
          setShowPopup(false);
          if (logoutTimeoutRef.current) {
            clearTimeout(logoutTimeoutRef.current);
            logoutTimeoutRef.current = null;
          }
          dispatch(signOut());
        } else {
          // Check if we need to refresh token when tab becomes active
          const timeUntilExpiry = expirationTimeRef.current - currentTime;
          if (timeUntilExpiry < AUTO_REFRESH_BEFORE_EXPIRY && !isUserInactive()) {
            refreshUserToken();
          }
        }
      }
    } else {
      isActiveRef.current = false;
    }
  };

  // Setup activity listeners
  useEffect(() => {
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];

    const handleActivity = () => {
      updateActivity();
    };

    // Add activity listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Check token on mount
  useEffect(() => {
    if (token) {
      const expirationTime = getTokenExpiration(token);
      if (expirationTime) {
        const currentTime = Date.now();
        if (currentTime >= expirationTime) {
          dispatch(signOut());
        } else {
          setupExpirationHandling(token);
        }
      }
    }
  }, []);

  // Listen for token changes
  useEffect(() => {
    if (token) {
      const sessionToken = sessionStorage.getItem("token");
      if (sessionToken !== token) {
        sessionStorage.setItem("token", token);
      }

      setupExpirationHandling(token);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
        logoutTimeoutRef.current = null;
      }
      if (autoRefreshTimeoutRef.current) {
        clearTimeout(autoRefreshTimeoutRef.current);
        autoRefreshTimeoutRef.current = null;
      }
    };
  }, [token, dispatch]);

  // Regular activity and expiration check
  useEffect(() => {
    const checkInterval = setInterval(() => {
      if (expirationTimeRef.current) {
        const currentTime = Date.now();

        // Check if token expired
        if (currentTime >= expirationTimeRef.current) {
          setShowPopup(false);
          if (logoutTimeoutRef.current) {
            clearTimeout(logoutTimeoutRef.current);
            logoutTimeoutRef.current = null;
          }
          dispatch(signOut());
        } else {
          // Check if we should auto-refresh for active users
          const timeUntilExpiry = expirationTimeRef.current - currentTime;
          if (timeUntilExpiry < AUTO_REFRESH_BEFORE_EXPIRY &&
              timeUntilExpiry > (AUTO_REFRESH_BEFORE_EXPIRY - ACTIVITY_CHECK_INTERVAL) &&
              !isUserInactive() &&
              isActiveRef.current &&
              !autoRefreshTimeoutRef.current) {
            refreshUserToken();
          }
        }
      }
    }, ACTIVITY_CHECK_INTERVAL);

    return () => clearInterval(checkInterval);
  }, []);

  // Session continuation popup component
  const SessionContinuationPopup = () => (
    <Dialog
      open={showPopup}
      aria-labelledby="session-dialog-title"
      aria-describedby="session-dialog-description"
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
      style={{ zIndex: 9999 }}
    >
      <DialogTitle id="session-dialog-title">Session Timeout Warning</DialogTitle>
      <DialogContent>
        <DialogContentText id="session-dialog-description">
          Your session is about to expire due to inactivity.
          Would you like to continue your session?
          <br />
          <br />
          Session will expire in 60 seconds.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => {
          setShowPopup(false);
          if (logoutTimeoutRef.current) {
            clearTimeout(logoutTimeoutRef.current);
            logoutTimeoutRef.current = null;
          }
          dispatch(signOut());
        }} color="secondary">
          Logout
        </Button>
        <Button onClick={handleContinueSession} className="btn-primary" autoFocus>
          Continue Session
        </Button>
      </DialogActions>
    </Dialog>
  );

  return {
    SessionContinuationPopup
  };
};